import NewsFeed from './components/NewsFeed';
import WatchlistNewsFeed from './components/WatchlistNewsFeed';
import UserProfile from './components/UserProfile';
import StockDetail from './components/StockDetail';
import ThemeDetail from './components/ThemeDetail';
import Watchlist from './components/Watchlist';
import ThemesPage from './components/ThemesPage';
import NewsFeedPage from './components/NewsFeedPage';
import UserProfilePopup from './components/UserProfilePopup';
import UserManagement from './components/UserManagement';
import Friends from './components/Friends';
import FriendsList from './components/FriendsList';
import ChatPopup from './components/ChatPopup';
import AdSense from './components/AdSense';
import CompanyLogo from './components/CompanyLogo';
import StockEducation from './components/StockEducation';
import LanguageSwitcher from './components/LanguageSwitcher';
import AuthModal from './components/AuthModal';
import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Heart, MessageCircle, Share2, TrendingUp, TrendingDown, BarChart3, Bell, Home, Users, Search, Image, MoreHorizontal, Mail, Lock, User, Eye, EyeOff, LogOut, Star, Newspaper, Smile, Activity, X, Sparkles, BookOpen } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, getDocs, query, orderBy, updateDoc, doc, arrayUnion, arrayRemove, serverTimestamp, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase/config';
import { signUpWithEmail, signInWithEmail, signInWithGoogle, logout } from './firebase/auth';
import { canCreatePost, canLikePost, USER_ROLES } from './utils/roles';
import { getDefaultAvatar } from './utils/avatars';
import CompanyManagement from './components/CompanyManagement';

export default function MijuSotong() {
  console.log('🚀 MijuSotong App component rendering...');
  const { t } = useTranslation();
  console.log('✅ i18n loaded, current language:', t && 'working');
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestMode, setGuestMode] = useState(false);
  const [guestWatchlist, setGuestWatchlist] = useState([]);
  const [showLogin, setShowLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  // Modal state
  const [authModal, setAuthModal] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    showResendButton: false,
    email: '',
    onResend: null
  });

  // Initialize currentView from URL - use function that accesses location safely
  const getViewFromPath = (pathname) => {
    const path = pathname || location.pathname;
    if (path === '/' || path === '/feed') return 'feed';
    if (path === '/profile') return 'profile';
    if (path === '/watchlist') return 'watchlist';
    if (path === '/news') return 'news';
    if (path === '/friends') return 'friends';
    if (path === '/themes') return 'themes';
    if (path === '/companies') return 'companies';
    if (path === '/education') return 'education';
    if (path.startsWith('/stock/')) return 'stock';
    if (path.startsWith('/theme/')) return 'theme';
    return 'feed';
  };

  // Extract initial stock/theme from URL
  const getInitialStock = (pathname) => {
    const path = pathname || location.pathname;
    if (path.startsWith('/stock/')) {
      return path.split('/')[2] || null;
    }
    return null;
  };

  const getInitialTheme = (pathname) => {
    const path = pathname || location.pathname;
    if (path.startsWith('/theme/')) {
      return path.split('/')[2] || null;
    }
    return null;
  };

  const [currentView, setCurrentView] = useState(() => getViewFromPath(location.pathname));
  const [selectedStock, setSelectedStock] = useState(() => getInitialStock(location.pathname));
  const [selectedTheme, setSelectedTheme] = useState(() => getInitialTheme(location.pathname));
  const [showNewPost, setShowNewPost] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPostText, setNewPostText] = useState('');
  const [newPostPrediction, setNewPostPrediction] = useState('bullish');
  const [newPostTicker, setNewPostTicker] = useState('');
  const [newPostImages, setNewPostImages] = useState([]);
  const [newPostFeeling, setNewPostFeeling] = useState('');
  const [newPostActivity, setNewPostActivity] = useState('');
  const [showFeelingPicker, setShowFeelingPicker] = useState(false);
  const [showActivityPicker, setShowActivityPicker] = useState(false);
  const [posts, setPosts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [prices, setPrices] = useState({});
  const [userRole, setUserRole] = useState(USER_ROLES.GUEST);
  const [userAvatar, setUserAvatar] = useState(getDefaultAvatar());
  const [userThemes, setUserThemes] = useState([]);
  const [openChats, setOpenChats] = useState([]);
  const [minimizedChats, setMinimizedChats] = useState([]);
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [selectedNewsTicker, setSelectedNewsTicker] = useState('PLUG');

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Theme definitions (same as in Watchlist.js and ThemeDetail.js)
  const themes = {
    ai: { name: 'AI', description: 'Artificial Intelligence companies', companies: ['AMD', 'AMZN', 'GOOGL', 'META', 'MSFT', 'NVDA', 'ORCL', 'PLTR', 'TSLA'] },
    biotech: { name: 'Biotech', description: 'Biotechnology companies', companies: ['ABBV', 'AMGN', 'BIIB', 'GILD', 'JNJ', 'LLY', 'MRNA', 'PFE', 'REGN', 'VRTX'] },
    ev: { name: 'Electric Vehicle', description: 'Electric vehicle manufacturers', companies: ['F', 'GM', 'LCID', 'NIO', 'RIVN', 'TSLA'] },
    hydrogen: { name: 'Hydrogen', description: 'Hydrogen energy companies', companies: ['BE', 'BLDP', 'FCEL', 'PLUG'] },
    nuclear: { name: 'Nuclear', description: 'Nuclear energy companies', companies: ['CCJ', 'DNN', 'NEE', 'NXE', 'SMR', 'UEC'] },
    quantum: { name: 'Quantum Computing', description: 'Quantum computing companies', companies: ['GOOGL', 'IONQ', 'IBM', 'MSFT', 'RGTI'] },
    solar: { name: 'Solar', description: 'Solar energy companies', companies: ['CSIQ', 'ENPH', 'FSLR', 'RUN', 'SEDG', 'SPWR'] }
  };

  // Use ref to prevent circular updates between URL and state
  const isUpdatingFromUrl = useRef(false);
  const isUpdatingFromState = useRef(false);

  // Sync URL when currentView, selectedStock, or selectedTheme changes
  useEffect(() => {
    if (isUpdatingFromUrl.current) return;

    isUpdatingFromState.current = true;

    let newPath = '/';
    if (currentView === 'feed') newPath = '/';
    else if (currentView === 'profile') newPath = '/profile';
    else if (currentView === 'watchlist') newPath = '/watchlist';
    else if (currentView === 'news') newPath = '/news';
    else if (currentView === 'friends') newPath = '/friends';
    else if (currentView === 'themes') newPath = '/themes';
    else if (currentView === 'companies') newPath = '/companies';
    else if (currentView === 'education') newPath = '/education';
    else if (currentView === 'stock' && selectedStock) newPath = `/stock/${selectedStock}`;
    else if (currentView === 'theme' && selectedTheme) newPath = `/theme/${selectedTheme}`;

    if (location.pathname !== newPath) {
      navigate(newPath, { replace: false });
    }

    setTimeout(() => { isUpdatingFromState.current = false; }, 0);
  }, [currentView, selectedStock, selectedTheme]);

  // Sync state when URL changes (browser back/forward)
  useEffect(() => {
    if (isUpdatingFromState.current) return;

    isUpdatingFromUrl.current = true;

    const view = getViewFromPath(location.pathname);
    if (view !== currentView) {
      setCurrentView(view);
    }

    // Extract stock/theme from URL
    if (location.pathname.startsWith('/stock/')) {
      const ticker = location.pathname.split('/')[2];
      if (ticker && ticker !== selectedStock) {
        setSelectedStock(ticker);
      }
    } else if (location.pathname.startsWith('/theme/')) {
      const themeKey = location.pathname.split('/')[2];
      if (themeKey && themeKey !== selectedTheme) {
        setSelectedTheme(themeKey);
      }
    }

    setTimeout(() => { isUpdatingFromUrl.current = false; }, 0);
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        loadPosts();
        loadCompanies();
        loadUserThemes(currentUser);
        setCurrentView('feed');
        await loadUserRole(currentUser);
      } else {
        setUserRole(USER_ROLES.GUEST);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadUserRole = async (currentUser) => {
    
    if (!currentUser) {
      setUserRole(USER_ROLES.GUEST);
      setUserAvatar(getDefaultAvatar());
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const role = userData.userRole || USER_ROLES.MEMBER;
        setUserRole(role);
        setUserAvatar(userData.avatar || getDefaultAvatar());
      } else {
        setUserRole(USER_ROLES.MEMBER); // Default for authenticated users
        setUserAvatar(getDefaultAvatar());
      }
    } catch (error) {
      console.error('🔍 [loadUserRole] Error loading user role:', error);
      setUserRole(USER_ROLES.MEMBER); // Default on error
      setUserAvatar(getDefaultAvatar());
    }
  };

  // Monitor userRole changes for debugging
  useEffect(() => {
  }, [userRole]);

  // Reload posts and watchlist when navigating back to feed view
  useEffect(() => {
    if (user && currentView === 'feed') {
      loadPosts();
      loadCompanies();
      loadUserThemes(user);
    } else if (guestMode && currentView === 'feed') {
      // Load companies and posts for guest mode
      loadCompanies();
      loadPosts(); // Load posts filtered by guest watchlist
    }
  }, [currentView, user, guestMode, guestWatchlist]);

  // Poll for new messages and auto-open chat if needed
  useEffect(() => {
    if (!user) return;

    const checkForNewMessages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/messages/conversations/${user.uid}`);
        const data = await response.json();

        console.log('Checking for new messages:', data);

        if (data.success && data.conversations) {
          // Check for conversations with unread messages
          data.conversations.forEach(async (conv) => {
            console.log('Conversation:', conv.otherUserName, 'Unread:', conv.unreadCount);

            // If there are unread messages and chat is not already open
            const isAlreadyOpen = openChats.find(c => c.otherUserId === conv.otherUserId);
            console.log('Is chat already open?', isAlreadyOpen);

            if (conv.unreadCount > 0 && !isAlreadyOpen) {
              console.log('Auto-opening chat with:', conv.otherUserName);

              // Auto-open the chat window
              const conversation = {
                otherUserId: conv.otherUserId,
                otherUserName: conv.otherUserName,
                otherUserAvatar: conv.otherUserAvatar,
                otherUserUsername: conv.otherUserUsername
              };
              setOpenChats(prev => {
                // Double-check not already in the list
                if (prev.find(c => c.otherUserId === conv.otherUserId)) {
                  return prev;
                }
                return [...prev, conversation];
              });
            }
          });
        }
      } catch (error) {
        console.error('Error checking for new messages:', error);
      }
    };

    // Check immediately
    checkForNewMessages();

    // Then check every 3 seconds (reduced from 5 for faster response)
    const interval = setInterval(checkForNewMessages, 3000);

    return () => clearInterval(interval);
  }, [user, openChats]);

  const loadUserThemes = async (currentUser) => {
    if (!currentUser) {
      setUserThemes([]);
      return;
    }

    try {
      // Get user's watchlist theme IDs from Firestore
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        setUserThemes([]);
        return;
      }

      const watchlistThemeIds = userDoc.data().watchlistThemes || [];

      // Load full theme data from Firestore
      const themesCollection = collection(db, 'investmentThemes');
      const themesSnapshot = await getDocs(themesCollection);
      const allThemes = themesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter to only user's watchlist themes
      const userWatchlistThemes = allThemes.filter(theme =>
        watchlistThemeIds.includes(theme.id)
      ).map(theme => ({
        id: theme.id,
        name: theme.name,
        description: theme.description,
        companies: theme.stocks?.map(s => s.ticker) || []
      }));

      setUserThemes(userWatchlistThemes);
    } catch (error) {
      console.error('Error loading user themes:', error);
      setUserThemes([]);
    }
  };

  const loadPosts = async () => {
    try {
      const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      let postsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // For guest users, filter posts to show only discussions about watchlist stocks
      if (!user && guestWatchlist && guestWatchlist.length > 0) {
        postsData = postsData.filter(post => guestWatchlist.includes(post.ticker));
        console.log('Filtered posts for guest watchlist:', postsData.length);
      }
      
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';

      // If user is logged in, load their watchlist
      if (user) {
        const response = await fetch(`${API_URL}/api/watchlist/${user.uid}/details`);
        const data = await response.json();
        if (data.success) {
          setCompanies(data.companies || []);

          // Set first watchlist item as default for news feed
          if (data.companies && data.companies.length > 0) {
            setSelectedNewsTicker(data.companies[0].ticker);
            loadPrices(data.companies.map(c => c.ticker));
          }
        }
      } else if (guestMode && guestWatchlist && guestWatchlist.length > 0) {
        // If in guest mode, load guest watchlist companies
        const response = await fetch(`${API_URL}/api/companies`);
        const data = await response.json();
        if (data.success) {
          const guestCompanies = data.companies.filter(c => guestWatchlist.includes(c.ticker));
          setCompanies(guestCompanies);

          // Set first watchlist item as default for news feed
          if (guestCompanies.length > 0) {
            setSelectedNewsTicker(guestCompanies[0].ticker);
            loadPrices(guestCompanies.map(c => c.ticker));
          }
        }
      } else {
        // If not logged in, show empty
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadPrices = async (tickers) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers })
      });
      const data = await response.json();
      if (data.success) {
        setPrices(data.prices || {});
      }
    } catch (error) {
      console.error('Error loading prices:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await signInWithEmail(loginForm.email, loginForm.password);
    if (result.success) {
      setAuthModal({
        isOpen: true,
        type: 'success',
        title: t('auth.loginSuccess'),
        message: t('auth.loginSuccessMessage'),
        showResendButton: false,
        email: '',
        onResend: null
      });
    } else {
      if (result.emailNotVerified) {
        const { resendVerificationEmail } = await import('./firebase/auth');
        setAuthModal({
          isOpen: true,
          type: 'warning',
          title: t('auth.errorTitle'),
          message: result.error,
          showResendButton: true,
          email: loginForm.email,
          onResend: async () => {
            const resendResult = await resendVerificationEmail(result.user);
            if (resendResult.success) {
              setAuthModal({
                isOpen: true,
                type: 'info',
                title: t('auth.successTitle'),
                message: t('auth.verificationEmailResent'),
                showResendButton: false,
                email: loginForm.email,
                onResend: null
              });
            } else {
              setAuthModal({
                isOpen: true,
                type: 'error',
                title: t('auth.errorTitle'),
                message: resendResult.error,
                showResendButton: false,
                email: '',
                onResend: null
              });
            }
          }
        });
        // Sign out the unverified user
        await logout();
      } else {
        setAuthModal({
          isOpen: true,
          type: 'error',
          title: t('auth.errorTitle'),
          message: result.error,
          showResendButton: false,
          email: '',
          onResend: null
        });
      }
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (signupForm.password !== signupForm.confirmPassword) {
      setAuthModal({
        isOpen: true,
        type: 'error',
        title: t('auth.passwordMismatch'),
        message: t('auth.passwordMismatchMessage'),
        showResendButton: false,
        email: '',
        onResend: null
      });
      return;
    }
    const result = await signUpWithEmail(signupForm.email, signupForm.password, signupForm.name);
    if (result.success) {
      // Sign out the user immediately after signup so they must verify email first
      await logout();

      // NOW show the modal AFTER logout completes
      // Manually replace {email} in the translated message
      const message = t('auth.signupSuccessMessage').replace('{email}', signupForm.email);
      setAuthModal({
        isOpen: true,
        type: 'info',
        title: t('auth.signupSuccessTitle'),
        message: message,
        showResendButton: false,
        email: signupForm.email,
        onResend: null
      });
      // DO NOT switch to login view here - let user read the modal first
      // setShowLogin(true) will be called when they close the modal
    } else {
      setAuthModal({
        isOpen: true,
        type: 'error',
        title: t('auth.errorTitle'),
        message: result.error,
        showResendButton: false,
        email: '',
        onResend: null
      });
    }
  };

  const handleGoogleLogin = async () => {
    const result = await signInWithGoogle();
    if (!result.success) {
      setAuthModal({
        isOpen: true,
        type: 'error',
        title: t('auth.errorTitle'),
        message: result.error,
        showResendButton: false,
        email: '',
        onResend: null
      });
    }
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    setPosts([]);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setNewPostImages(prev => [...prev, ...imageUrls]);
  };

  const removeImage = (index) => {
    setNewPostImages(prev => prev.filter((_, i) => i !== index));
  };

  const feelings = [
    { emoji: '😊', label: '행복해요' },
    { emoji: '😍', label: '사랑해요' },
    { emoji: '😂', label: '웃겨요' },
    { emoji: '🥰', label: '설레요' },
    { emoji: '😎', label: '멋져요' },
    { emoji: '🤗', label: '감사해요' },
    { emoji: '😢', label: '슬퍼요' },
    { emoji: '😤', label: '화나요' },
    { emoji: '😴', label: '피곤해요' },
    { emoji: '🤔', label: '생각중이에요' },
    { emoji: '😋', label: '맛있어요' },
    { emoji: '🎉', label: '축하해요' },
    { emoji: '😌', label: '평화로워요' },
    { emoji: '😆', label: '신나요' },
    { emoji: '🥳', label: '파티해요' },
    { emoji: '😇', label: '천사같아요' },
    { emoji: '🤩', label: '감동했어요' },
    { emoji: '😱', label: '놀랐어요' },
    { emoji: '🥺', label: '애틋해요' },
    { emoji: '😭', label: '울어요' },
    { emoji: '😅', label: '민망해요' },
    { emoji: '🤤', label: '군침도네요' }
  ];

  const activities = [
    { emoji: '✈️', label: '여행 중' },
    { emoji: '🏋️', label: '운동 중' },
    { emoji: '📚', label: '공부 중' },
    { emoji: '🍽️', label: '식사 중' },
    { emoji: '☕', label: '카페에서' },
    { emoji: '🎮', label: '게임 중' },
    { emoji: '🎬', label: '영화 관람 중' },
    { emoji: '🎵', label: '음악 듣는 중' },
    { emoji: '🛒', label: '쇼핑 중' },
    { emoji: '🏠', label: '집에서' },
    { emoji: '💼', label: '일하는 중' },
    { emoji: '🚗', label: '이동 중' },
    { emoji: '🎨', label: '그림 그리는 중' },
    { emoji: '🍳', label: '요리하는 중' },
    { emoji: '📖', label: '독서 중' },
    { emoji: '🧘', label: '명상 중' },
    { emoji: '🏃', label: '달리는 중' },
    { emoji: '🎤', label: '노래하는 중' },
    { emoji: '💻', label: '코딩 중' },
    { emoji: '🎯', label: '목표 달성 중' },
    { emoji: '🌅', label: '산책 중' },
    { emoji: '🛌', label: '휴식 중' }
  ];

  const handleNewPost = async () => {
    if (!newPostText.trim()) {
      alert('내용을 입력해주세요!');
      return;
    }

    // Check if user has permission to post
    if (!canCreatePost(userRole)) {
      if (userRole === USER_ROLES.SUSPEND) {
        alert('Your account has been suspended. You cannot create posts.');
      } else if (userRole === USER_ROLES.GUEST) {
        alert('Guest accounts cannot create posts. Please contact an administrator to upgrade your account.');
      } else {
        alert('You do not have permission to create posts.');
      }
      return;
    }

    try {
      await addDoc(collection(db, 'posts'), {
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorAvatar: userAvatar,
        content: newPostText,
        prediction: newPostPrediction,
        ticker: newPostTicker.toUpperCase() || 'AAPL',
        images: newPostImages,
        feeling: newPostFeeling,
        activity: newPostActivity,
        likes: [],
        comments: [],
        shares: 0,
        createdAt: serverTimestamp()
      });
      setNewPostText('');
      setNewPostTicker('');
      setNewPostImages([]);
      setNewPostFeeling('');
      setNewPostActivity('');
      setShowNewPost(false);
      loadPosts();
      alert('✅ 게시물이 작성되었습니다!');
    } catch (error) {
      console.error('Error creating post:', error);
      alert('❌ 게시물 작성 실패');
    }
  };

  const handleLike = async (postId, likes) => {
    // Check if user is logged in
    if (!user) {
      alert('로그인 후 이용해주세요.');
      return;
    }

    // Check if user has permission to like posts
    if (!canLikePost(userRole)) {
      alert('You do not have permission to like posts.');
      return;
    }

    try {
      const postRef = doc(db, 'posts', postId);
      const hasLiked = likes.includes(user.uid);
      if (hasLiked) {
        await updateDoc(postRef, { likes: arrayRemove(user.uid) });
      } else {
        await updateDoc(postRef, { likes: arrayUnion(user.uid) });
      }
      loadPosts();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleAddComment = async (postId) => {
    const commentText = newComment[postId];
    console.log('Adding comment to post:', postId, 'Text:', commentText);

    if (!user) {
      alert('로그인 후 이용해주세요.');
      return;
    }

    if (!commentText || !commentText.trim()) {
      console.log('Comment is empty, not adding');
      return;
    }

    try {
      const postRef = doc(db, 'posts', postId);
      const comment = {
        id: Date.now().toString(),
        userId: user.uid,
        userName: user.displayName || user.email,
        userAvatar: userAvatar,
        text: commentText.trim(),
        createdAt: new Date()
      };

      console.log('Submitting comment:', comment);

      await updateDoc(postRef, {
        comments: arrayUnion(comment)
      });

      console.log('Comment added successfully');
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      loadPosts();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment: ' + error.message);
    }
  };

  const toggleComments = (postId) => {
    setShowComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '방금';
    const seconds = Math.floor((new Date() - timestamp.toDate()) / 1000);
    if (seconds < 60) return '방금';
    if (seconds < 3600) return Math.floor(seconds / 60) + '분 전';
    if (seconds < 86400) return Math.floor(seconds / 3600) + '시간 전';
    return Math.floor(seconds / 86400) + '일 전';
  };

  const handleOpenChat = async (userId) => {
    // Check if chat is already open
    if (openChats.find(chat => chat.otherUserId === userId)) {
      // If minimized, restore it
      setMinimizedChats(prev => prev.filter(id => id !== userId));
      return;
    }

    // Fetch user details
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.exists() ? userDoc.data() : {};

      const conversation = {
        otherUserId: userId,
        otherUserName: userData.displayName || 'User',
        otherUserAvatar: userData.avatar || '😊',
        otherUserUsername: userData.username || `@user${userId.substring(0, 6)}`
      };

      setOpenChats(prev => [...prev, conversation]);
    } catch (error) {
      console.error('Error opening chat:', error);
    }
  };

  const handleCloseChat = (userId) => {
    setOpenChats(prev => prev.filter(chat => chat.otherUserId !== userId));
    setMinimizedChats(prev => prev.filter(id => id !== userId));
  };

  const handleMinimizeChat = (userId) => {
    if (minimizedChats.includes(userId)) {
      setMinimizedChats(prev => prev.filter(id => id !== userId));
    } else {
      setMinimizedChats(prev => [...prev, userId]);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">로딩 중...</div>;
  }

  if (!user && !guestMode) {
    console.log('Rendering login screen - about to render LanguageSwitcher');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <div className="max-w-6xl w-full grid md:grid-cols-2 gap-8 items-center">
          <div className="text-center md:text-left">
            <h1 className="text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">{t('appName')}</h1>
            <p className="text-2xl text-gray-700 mb-6">전 세계 트레이더들과 연결되고<br />투자 아이디어를 공유하세요</p>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex gap-2 mb-6">
              <button onClick={() => setShowLogin(true)} className={'flex-1 py-3 rounded-lg font-medium transition-all ' + (showLogin ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600')}>로그인</button>
              <button onClick={() => setShowLogin(false)} className={'flex-1 py-3 rounded-lg font-medium transition-all ' + (!showLogin ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600')}>회원가입</button>
            </div>
            {showLogin ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" value={loginForm.email} onChange={(e) => setLoginForm({...loginForm, email: e.target.value})} autocomplete="email" placeholder="your@email.com" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">비밀번호</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type={showPassword ? 'text' : 'password'} value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} autocomplete="current-password" placeholder="••••••••" className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                  </div>
                </div>
                <button type="submit" className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all">로그인</button>
                <button type="button" onClick={handleGoogleLogin} className="w-full py-3 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-50 flex items-center justify-center gap-2"><span className="text-xl">🔐</span>Google로 계속하기</button>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
                  <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">또는</span></div>
                </div>
                <button type="button" onClick={() => {
                  setGuestMode(true);
                  setCurrentView('feed');
                  // Initialize Magnificent 7 stocks in guest watchlist
                  setGuestWatchlist(['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA']);
                  // Initialize Magnificent 7 theme for guest
                  setUserThemes([{
                    id: 'magnificent-7-guest',
                    name: 'Magnificent 7',
                    description: 'The seven largest technology companies: Apple, Microsoft, Google, Amazon, NVIDIA, Meta, and Tesla',
                    companies: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA']
                  }]);
                }} className="w-full py-3 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-100 text-gray-700 transition-all">게스트로 둘러보기</button>
              </form>
            ) : (
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">이름</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" value={signupForm.name} onChange={(e) => setSignupForm({...signupForm, name: e.target.value})} placeholder="홍길동" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" value={signupForm.email} onChange={(e) => setSignupForm({...signupForm, email: e.target.value})} autocomplete="email" placeholder="your@email.com" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">비밀번호</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="password" value={signupForm.password} onChange={(e) => setSignupForm({...signupForm, password: e.target.value})} autocomplete="new-password" placeholder="••••••••" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">비밀번호 확인</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="password" value={signupForm.confirmPassword} onChange={(e) => setSignupForm({...signupForm, confirmPassword: e.target.value})} autocomplete="new-password" placeholder="••••••••" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <button type="submit" className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all">회원가입</button>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
                  <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">또는</span></div>
                </div>
                <button type="button" onClick={() => {
                  setGuestMode(true);
                  setCurrentView('feed');
                  // Initialize Magnificent 7 stocks in guest watchlist
                  setGuestWatchlist(['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA']);
                  // Initialize Magnificent 7 theme for guest
                  setUserThemes([{
                    id: 'magnificent-7-guest',
                    name: 'Magnificent 7',
                    description: 'The seven largest technology companies: Apple, Microsoft, Google, Amazon, NVIDIA, Meta, and Tesla',
                    companies: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA']
                  }]);
                }} className="w-full py-3 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-100 text-gray-700 transition-all">게스트로 둘러보기</button>
              </form>
            )}
          </div>
        </div>

        {/* Auth Modal - MUST be included in login screen too! */}
        <AuthModal
          isOpen={authModal.isOpen}
          onClose={() => {
            setAuthModal({ ...authModal, isOpen: false });
            // If this was a signup success modal (type: 'info'), switch to login view
            if (authModal.type === 'info' && authModal.title === t('auth.signupSuccessTitle')) {
              setShowLogin(true);
            }
          }}
          type={authModal.type}
          title={authModal.title}
          message={authModal.message}
          showResendButton={authModal.showResendButton}
          onResend={authModal.onResend}
          email={authModal.email}
        />
      </div>
    );
  }

  // Profile view
  if (currentView === 'profile') {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  {console.log('📍 About to render LanguageSwitcher in header (logged in view)')}
                  <LanguageSwitcher />
                  {user ? (
                    <>
                      <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 ring-2 ring-blue-500">
                        {userAvatar && userAvatar.startsWith('data:image') ? (
                          <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-3xl">{userAvatar}</span>
                        )}
                      </button>
                      <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                        <LogOut className="w-5 h-5 text-gray-600" />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setGuestMode(false)} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all">
                      로그인
                    </button>
                  )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <UserProfile
          user={user}
          onAvatarUpdate={(newAvatar) => {
            setUserAvatar(newAvatar);
            loadPosts(); // Reload posts to show updated avatars
          }}
        />

        {/* Chat Popups */}
        {openChats.map((conversation, index) => (
          <div
            key={conversation.otherUserId}
            style={{ right: `${24 + index * 400}px` }}
          >
            <ChatPopup
              user={user}
              conversation={conversation}
              onClose={() => handleCloseChat(conversation.otherUserId)}
              onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
              isMinimized={minimizedChats.includes(conversation.otherUserId)}
            />
          </div>
        ))}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }

  // Watchlist view
  if (currentView === 'watchlist') {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6 fill-blue-600" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <Watchlist
          user={user}
          userRole={userRole}
          guestWatchlist={guestWatchlist}
          onSelectStock={(ticker) => {
            setSelectedStock(ticker);
            setCurrentView('stock');
          }}
          onSelectTheme={(themeKey) => {
            setSelectedTheme(themeKey);
            setCurrentView('theme');
          }}
        />

        {/* Chat Popups */}
        {openChats.map((conversation, index) => (
          <div
            key={conversation.otherUserId}
            style={{ right: `${24 + index * 400}px` }}
          >
            <ChatPopup
              user={user}
              conversation={conversation}
              onClose={() => handleCloseChat(conversation.otherUserId)}
              onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
              isMinimized={minimizedChats.includes(conversation.otherUserId)}
            />
          </div>
        ))}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }

  // News view
  if (currentView === 'news') {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6 fill-blue-600" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <NewsFeedPage user={user} userRole={userRole} />

        {/* Chat Popups */}
        {openChats.map((conversation, index) => (
          <div
            key={conversation.otherUserId}
            style={{ right: `${24 + index * 400}px` }}
          >
            <ChatPopup
              user={user}
              conversation={conversation}
              onClose={() => handleCloseChat(conversation.otherUserId)}
              onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
              isMinimized={minimizedChats.includes(conversation.otherUserId)}
            />
          </div>
        ))}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }

  // Friends view
  if (currentView === 'friends') {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6 fill-blue-600" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <Friends
          user={user}
          userRole={userRole}
          onStartMessage={handleOpenChat}
        />

        {/* Chat Popups */}
        {openChats.map((conversation, index) => (
          <div
            key={conversation.otherUserId}
            style={{ right: `${24 + index * 400}px` }}
          >
            <ChatPopup
              user={user}
              conversation={conversation}
              onClose={() => handleCloseChat(conversation.otherUserId)}
              onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
              isMinimized={minimizedChats.includes(conversation.otherUserId)}
            />
          </div>
        ))}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }

  // Stock Detail view
  if (currentView === 'stock' && selectedStock) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <StockDetail
          ticker={selectedStock}
          onBack={() => {
            navigate(-1); // Go back in browser history
          }}
        />

        {/* Chat Popups */}
        {openChats.map((conversation, index) => (
          <div
            key={conversation.otherUserId}
            style={{ right: `${24 + index * 400}px` }}
          >
            <ChatPopup
              user={user}
              conversation={conversation}
              onClose={() => handleCloseChat(conversation.otherUserId)}
              onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
              isMinimized={minimizedChats.includes(conversation.otherUserId)}
            />
          </div>
        ))}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }

  // Theme Detail view
  if (currentView === 'theme' && selectedTheme) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <ThemeDetail
          themeKey={selectedTheme}
          guestTheme={!user && userThemes.length > 0 ? userThemes.find(t => t.id === selectedTheme) : null}
          onStockClick={(ticker) => {
            setSelectedStock(ticker);
            setCurrentView("stock");
          }}
          onBack={() => {
            navigate(-1); // Go back in browser history
          }}
        />

        {/* Chat Popups */}
        {openChats.map((conversation, index) => (
          <div
            key={conversation.otherUserId}
            style={{ right: `${24 + index * 400}px` }}
          >
            <ChatPopup
              user={user}
              conversation={conversation}
              onClose={() => handleCloseChat(conversation.otherUserId)}
              onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
              isMinimized={minimizedChats.includes(conversation.otherUserId)}
            />
          </div>
        ))}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }

  // Themes view
  if (currentView === 'themes') {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6 fill-blue-600" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <ThemesPage
          currentUser={user}
          guestThemes={!user ? userThemes : []}
          onSelectTheme={(themeKey) => {
            setSelectedTheme(themeKey);
            setCurrentView('theme');
          }}
        />

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
}


  // Companies view (Admin only)
  if (currentView === 'companies') {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView('companies')} className="flex flex-col items-center gap-1 text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <CompanyManagement user={user} userRole={userRole} />

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }

  // Education view (미주공부방)
  if (currentView === 'education') {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              </div>
              <nav className="flex items-center gap-6">
                <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.home')}>
                  <Home className="w-6 h-6" />
                  <span className="text-xs">{t('nav.home')}</span>
                </button>
                <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                  <Star className="w-6 h-6" />
                  <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6 fill-blue-600" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView('companies')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
                </div>
              </nav>
            </div>
          </div>
        </header>
        <StockEducation />

        {/* Chat Popups */}
        {openChats.map((conversation, index) => (
          <div
            key={conversation.otherUserId}
            style={{ right: `${24 + index * 400}px` }}
          >
            <ChatPopup
              user={user}
              conversation={conversation}
              onClose={() => handleCloseChat(conversation.otherUserId)}
              onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
              isMinimized={minimizedChats.includes(conversation.otherUserId)}
            />
          </div>
        ))}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('appName')}</div>
              <div className="ml-4 flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="text" placeholder="종목, 트레이더 검색..." className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
            <nav className="flex items-center gap-6">
              <button onClick={() => setCurrentView('feed')} className="flex flex-col items-center gap-1 text-blue-600" title={t('nav.home')}>
                <Home className="w-6 h-6 fill-blue-600" />
                <span className="text-xs">{t('nav.home')}</span>
              </button>
              <button onClick={() => setCurrentView('watchlist')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.watchlist')}>
                <Star className="w-6 h-6" />
                <span className="text-xs">{t('nav.watchlist')}</span>
                </button>
                <button onClick={() => setCurrentView('themes')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.themes')}>
                  <Sparkles className="w-6 h-6" />
                  <span className="text-xs">{t('nav.themes')}</span>
                </button>
                <button onClick={() => setCurrentView('news')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.news')}>
                  <Newspaper className="w-6 h-6" />
                  <span className="text-xs">{t('nav.news')}</span>
                </button>
                <button onClick={() => setCurrentView('education')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.education')}>
                  <BookOpen className="w-6 h-6" />
                  <span className="text-xs">{t('nav.education')}</span>
                </button>
                <button onClick={() => setCurrentView('friends')} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title={t('nav.friends')}>
                  <Users className="w-6 h-6" />
                  <span className="text-xs">{t('nav.friends')}</span>
                </button>
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setCurrentView("companies")} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </button>
                )}
                {userRole === USER_ROLES.ADMIN && (
                  <button onClick={() => setShowUserManagement(true)} className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-600" title="사용자 관리">
                    <Users className="w-6 h-6" />
                  </button>
                )}
                <button className="relative flex flex-col items-center gap-1 text-gray-600">
                  <Bell className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                  {console.log('📍 About to render LanguageSwitcher in MAIN FEED header')}
                  <LanguageSwitcher />
                {user ? (
                  <>
                    <button onClick={() => setCurrentView('profile')} className="cursor-pointer hover:opacity-80 w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                      {userAvatar && userAvatar.startsWith('data:image') ? (
                        <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl">{userAvatar}</span>
                      )}
                    </button>
                    <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-full">
                      <LogOut className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                  >
                    회원가입 / 로그인
                  </button>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          <aside className="col-span-3">
            <div className="space-y-4">
              {/* User Themes */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-lg">{t('sidebar.myThemes')}</h3>
                  {userThemes.length > 0 && (
                    <button
                      onClick={() => setCurrentView('themes')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      {t('sidebar.viewAll')}
                    </button>
                  )}
                </div>
                {userThemes.length === 0 ? (
                  <div className="text-center py-4">
                    <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-2">No themes selected</p>
                    <button
                      onClick={() => setCurrentView('themes')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      + Add Themes
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[280px] overflow-y-auto">
                    {userThemes.map((theme) => (
                      <div
                        key={theme.id}
                        onClick={() => {
                          setSelectedTheme(theme.id);
                          setCurrentView('theme');
                        }}
                        className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-bold text-base text-gray-900">{theme.name}</div>
                          <div className="text-xs text-gray-600">{theme.companies.length}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Companies Watchlist */}
              <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg">{t('sidebar.myWatchlist')}</h3>
                <button
                  onClick={() => setCurrentView('watchlist')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  {t('sidebar.viewAll')}
                </button>
              </div>

              {companies.length === 0 ? (
                <div className="text-center py-6">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 mb-3">No stocks in watchlist</p>
                  <button
                    onClick={() => setCurrentView('watchlist')}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + Add Stocks to Watch
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                  {companies.map((company) => {
                    const priceData = prices[company.ticker];
                    return (
                      <div
                        key={company.id}
                        onClick={() => {
                          setSelectedStock(company.ticker);
                          setCurrentView('stock');
                        }}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition"
                      >
                        {/* Company Logo */}
                        <CompanyLogo ticker={company.ticker} size="sm" />

                        {/* Ticker */}
                        <div className="flex items-center gap-2 w-20">
                          <span className="font-bold text-base text-gray-900">{company.ticker}</span>
                          {company.isActive && (
                            <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                          )}
                        </div>

                        {/* Trend Icon */}
                        <div className="flex-shrink-0">
                          {priceData ? (
                            priceData.change >= 0 ? (
                              <TrendingUp className="w-5 h-5 text-green-600" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-600" />
                            )
                          ) : (
                            <div className="w-5 h-5"></div>
                          )}
                        </div>

                        {/* Price */}
                        <div className="flex-1 text-right">
                          {priceData ? (
                            <div>
                              <div className="text-sm font-bold text-gray-900">
                                ${priceData.price.toFixed(2)}
                              </div>
                              <div className={`text-xs font-medium ${
                                priceData.change >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {priceData.change >= 0 ? '+' : ''}{priceData.changePercent.toFixed(2)}%
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">Loading...</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
            </div>
          </aside>
          <main className="col-span-6">
            {/* Show different prompt for guests vs members */}
            {user ? (
              canCreatePost(userRole) && (
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                  <button onClick={() => setShowNewPost(true)} className="w-full text-left px-4 py-3 bg-gray-100 rounded-full text-gray-500 hover:bg-gray-200">
                    {user.displayName || user.email}, 무슨 생각을 하고 계신가요?
                  </button>
                </div>
              )
            ) : (
              <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="w-full text-center px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                  <p className="text-gray-700 font-medium mb-2">메시지를 공유하려면 회원가입을 해주세요</p>
                  <button 
                    onClick={() => setGuestMode(false)} 
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium"
                  >
                    회원가입 / 로그인
                  </button>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {posts.map((post, index) => {
                const showAdAfter = (index + 1) % 3 === 0; // Show ad every 3 posts
                return (
                  <Fragment key={post.id}>
                    <article className="bg-white rounded-lg shadow p-4">
                    <div className="flex gap-3">
                    {/* Left side: Avatar, User name, Stock ticker, and Prediction */}
                    <div className="flex flex-col gap-2">
                      {/* Line 1: Avatar + User name (centered relative to line 2) */}
                      <div className="flex items-center justify-center gap-4">
                        <div
                          className="text-3xl cursor-pointer hover:opacity-80 transition"
                          onClick={() => setSelectedUserId(post.authorId)}
                          title="View profile"
                        >
                          {post.authorAvatar}
                        </div>
                        <div>
                          <button
                            onClick={() => setSelectedUserId(post.authorId)}
                            className="font-bold text-sm hover:text-blue-600 transition-colors cursor-pointer"
                          >
                            {post.authorName}
                          </button>
                          <p className="text-xs text-gray-500">{getTimeAgo(post.createdAt)}</p>
                        </div>
                      </div>
                      {/* Line 2: Stock ticker + Prediction */}
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">${post.ticker}</span>
                        <span className={'inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ' + (post.prediction === 'bullish' ? 'text-green-500 bg-green-500/10' : post.prediction === 'bearish' ? 'text-red-500 bg-red-500/10' : 'text-yellow-500 bg-yellow-500/10')}>
                          {post.prediction === 'bullish' ? <TrendingUp className="w-4 h-4" /> : post.prediction === 'bearish' ? <TrendingDown className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
                          {post.prediction === 'bullish' ? '강세' : post.prediction === 'bearish' ? '약세' : '중립'}
                        </span>
                      </div>
                    </div>
                    {/* Right side: Content */}
                    <div className="flex-1">
                      <p className="leading-relaxed text-gray-800">{post.content}</p>

                      {/* Feeling/Activity Tags */}
                      {(post.feeling || post.activity) && (
                        <div className="mt-2 flex gap-2">
                          {post.feeling && (
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs flex items-center gap-1">
                              <Smile className="w-3 h-3" />
                              {post.feeling}
                            </span>
                          )}
                          {post.activity && (
                            <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs flex items-center gap-1">
                              <Activity className="w-3 h-3" />
                              {post.activity}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Images */}
                      {post.images && post.images.length > 0 && (
                        <div className={`mt-3 grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                          {post.images.map((img, index) => (
                            <img
                              key={index}
                              src={img}
                              alt={`Post image ${index + 1}`}
                              className="w-full rounded-lg object-cover"
                              style={{ maxHeight: post.images.length === 1 ? '400px' : '200px' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Bottom: Actions with counts */}
                  <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-around">
                    <button onClick={() => handleLike(post.id, post.likes || [])} className={'flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-gray-100 ' + ((user && (post.likes || []).includes(user.uid)) ? 'text-red-500' : 'text-gray-600')}>
                      <Heart className={'w-4 h-4 ' + ((user && (post.likes || []).includes(user.uid)) ? 'fill-current' : '')} />
                      <span className="text-sm">좋아요</span>
                      {(post.likes || []).length > 0 && (
                        <span className="text-xs">({(post.likes || []).length})</span>
                      )}
                    </button>
                    <button onClick={() => toggleComments(post.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-gray-100 text-gray-600">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">댓글</span>
                      {(post.comments || []).length > 0 && (
                        <span className="text-xs">({(post.comments || []).length})</span>
                      )}
                    </button>
                    <button className="flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-gray-100 text-gray-600">
                      <Share2 className="w-4 h-4" />
                      <span className="text-sm">공유</span>
                    </button>
                  </div>

                  {/* Comments Section */}
                  {showComments[post.id] && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      {/* Existing Comments */}
                      {(post.comments || []).map((comment) => (
                        <div key={comment.id} className="flex gap-3 mb-3">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                            <span className="text-lg">{comment.userAvatar}</span>
                          </div>
                          <div className="flex-1">
                            <div className="bg-gray-100 rounded-lg px-3 py-2">
                              <div className="font-semibold text-sm">{comment.userName}</div>
                              <p className="text-sm text-gray-800">{comment.text}</p>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 ml-3">
                              {comment.createdAt ? getTimeAgo(comment.createdAt) : '방금'}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Add Comment */}
                      <div className="flex gap-3 mt-4">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                          <span className="text-lg">{userAvatar}</span>
                        </div>
                        <div className="flex-1 flex gap-2">
                          <input
                            type="text"
                            placeholder="댓글을 입력하세요..."
                            value={newComment[post.id] || ''}
                            onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddComment(post.id);
                              }
                            }}
                            className="flex-1 px-3 py-2 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                          <button
                            onClick={() => handleAddComment(post.id)}
                            disabled={!newComment[post.id]?.trim()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            게시
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
                {showAdAfter && (
                  <div className="bg-white rounded-lg shadow p-4 h-[150px] overflow-hidden flex items-center justify-center">
                    <AdSense
                      slot="5107198814"
                    />
                  </div>
                )}
                </Fragment>
                );
              })}
            </div>
          </main>
          <aside className="col-span-3">
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <h3 className="font-bold mb-4">친구 목록</h3>
              <FriendsList
                user={user}
                onViewAll={() => setCurrentView('friends')}
                onMessage={handleOpenChat}
              />
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">{t('sidebar.individualStockNews')}</h3>
                <div className="flex items-center gap-3">
                  {companies.length > 0 && (
                    <>
                      <select
                        value={selectedNewsTicker}
                        onChange={(e) => setSelectedNewsTicker(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {companies.map((company) => (
                          <option key={company.ticker} value={company.ticker}>
                            {company.ticker}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => {
                          setSelectedStock(selectedNewsTicker);
                          setCurrentView('stock');
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View All
                      </button>
                    </>
                  )}
                </div>
              </div>
              {companies.length > 0 ? (
                <NewsFeed ticker={selectedNewsTicker} userRole={userRole} compact={true} />
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  {t('sidebar.addWatchlistStocks')}
                </p>
              )}
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-bold mb-4">{t('sidebar.followRecommendations')}</h3>
            </div>
          </aside>
        </div>
      </div>

      {/* User Profile Popup */}
      {selectedUserId && (
        <UserProfilePopup
          userId={selectedUserId}
          currentUserId={user?.uid}
          onClose={() => setSelectedUserId(null)}
        />
      )}

      {/* User Management Modal - Admin Only */}
      {showUserManagement && userRole === USER_ROLES.ADMIN && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}

      {/* Chat Popups */}
      {openChats.map((conversation, index) => (
        <div
          key={conversation.otherUserId}
          style={{ right: `${24 + index * 400}px` }}
        >
          <ChatPopup
            user={user}
            conversation={conversation}
            onClose={() => handleCloseChat(conversation.otherUserId)}
            onMinimize={() => handleMinimizeChat(conversation.otherUserId)}
            isMinimized={minimizedChats.includes(conversation.otherUserId)}
          />
        </div>
      ))}

      {/* New Post Modal */}
      {showNewPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowNewPost(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4 relative" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">게시물 작성</h2>
              <button
                onClick={() => setShowNewPost(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Top Section: User Info and Stock/Prediction Selection */}
              <div className="flex gap-4 mb-4">
                {/* User Info */}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                    <span className="text-3xl">{userAvatar}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-base">{user.displayName || user.email}</h4>
                    <p className="text-sm text-gray-500">공개 게시물</p>
                  </div>
                </div>

                {/* Stock Selection and Prediction */}
                <div className="flex gap-8 ml-auto">
                  <div>
                    <select
                      value={newPostTicker}
                      onChange={(e) => setNewPostTicker(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">종목 선택</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.ticker}>
                          {company.ticker}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <select
                      value={newPostPrediction}
                      onChange={(e) => setNewPostPrediction(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bullish">🟢 강세</option>
                      <option value="neutral">🟡 중립</option>
                      <option value="bearish">🔴 약세</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <textarea
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
                placeholder="무슨 생각을 하고 계신가요?"
                className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed resize-none"
                rows="6"
                autoFocus
              />

              {/* Image Preview */}
              {newPostImages.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {newPostImages.map((img, index) => (
                    <div key={index} className="relative">
                      <img src={img} alt={`Upload ${index + 1}`} className="w-full h-48 object-cover rounded-lg" />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-gray-800 bg-opacity-70 text-white rounded-full hover:bg-opacity-90"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Feeling/Activity Display */}
              {(newPostFeeling || newPostActivity) && (
                <div className="mt-3 flex gap-2">
                  {newPostFeeling && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      <Smile className="w-4 h-4" />
                      {newPostFeeling}
                      <button onClick={() => setNewPostFeeling('')} className="ml-1 hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {newPostActivity && (
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm flex items-center gap-1">
                      <Activity className="w-4 h-4" />
                      {newPostActivity}
                      <button onClick={() => setNewPostActivity('')} className="ml-1 hover:text-green-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}

              {/* Add to Post Options */}
              <div className="mt-4 p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">게시물에 추가:</span>
                  <div className="flex gap-2">
                    {/* Photo/GIF Upload */}
                    <label className="cursor-pointer p-2 hover:bg-gray-100 rounded-lg transition" title="사진/GIF">
                      <Image className="w-5 h-5 text-green-600" />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>

                    {/* Feeling */}
                    <button
                      onClick={() => {
                        setShowFeelingPicker(!showFeelingPicker);
                        setShowActivityPicker(false);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="기분"
                    >
                      <Smile className="w-5 h-5 text-yellow-600" />
                    </button>

                    {/* Activity */}
                    <button
                      onClick={() => {
                        setShowActivityPicker(!showActivityPicker);
                        setShowFeelingPicker(false);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="활동"
                    >
                      <Activity className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feeling Picker Dropdown - Centered in Modal */}
            {showFeelingPicker && (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-30">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">기분을 선택하세요</h4>
                <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                  {feelings.map((feeling) => (
                    <button
                      key={feeling.label}
                      onClick={() => {
                        setNewPostFeeling(`${feeling.emoji} ${feeling.label}`);
                        setShowFeelingPicker(false);
                      }}
                      className="flex items-center gap-2 p-3 hover:bg-gray-100 rounded-lg transition text-left"
                    >
                      <span className="text-2xl">{feeling.emoji}</span>
                      <span className="text-sm">{feeling.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Picker Dropdown - Centered in Modal */}
            {showActivityPicker && (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-30">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">활동을 선택하세요</h4>
                <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                  {activities.map((activity) => (
                    <button
                      key={activity.label}
                      onClick={() => {
                        setNewPostActivity(`${activity.emoji} ${activity.label}`);
                        setShowActivityPicker(false);
                      }}
                      className="flex items-center gap-2 p-3 hover:bg-gray-100 rounded-lg transition text-left"
                    >
                      <span className="text-2xl">{activity.emoji}</span>
                      <span className="text-sm">{activity.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowNewPost(false)}
                className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition font-medium"
              >
                취소
              </button>
              <button
                onClick={handleNewPost}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                게시
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModal.isOpen}
        onClose={() => {
          setAuthModal({ ...authModal, isOpen: false });
          // If this was a signup success modal (type: 'info'), switch to login view
          if (authModal.type === 'info' && authModal.title === t('auth.signupSuccessTitle')) {
            setShowLogin(true);
          }
        }}
        type={authModal.type}
        title={authModal.title}
        message={authModal.message}
        showResendButton={authModal.showResendButton}
        onResend={authModal.onResend}
        email={authModal.email}
      />
    </div>
  );
}
